import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  Add01Icon,
  Building03Icon,
  BuildingIcon,
  ContactBookIcon,
  ContactIcon,
  DocumentValidationIcon,
  FileEditIcon,
  Invoice01Icon,
  Invoice02Icon,
  Package01Icon,
  ShoppingCart01Icon,
  Store01Icon,
  UserAdd01Icon,
  UserGroupIcon,
  UserIcon,
} from "@hugeicons/core-free-icons"
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import { Badge } from "@/components/ui/badge"
import { useAuth } from "@/lib/auth/use-auth"
import { useDebounce } from "@/lib/hooks/use-debounce"
import { useGlobalSearch } from "@/features/search/queries/search-queries"
import { useSearchStore } from "@/features/search/stores/search-store"
import type {
  SearchResultItem,
  SearchResults,
  SearchResultType,
} from "@/features/search/types/search.types"
import { CreateOrderDialog } from "@/features/orders/components/create-order-dialog"

const TYPE_ICONS: Record<SearchResultType, typeof UserIcon> = {
  order: ShoppingCart01Icon,
  project: Building03Icon,
  proposal: DocumentValidationIcon,
  change_order: FileEditIcon,
  account: UserGroupIcon,
  vendor: Store01Icon,
  contact: ContactIcon,
  customer: UserIcon,
  company: BuildingIcon,
  product: Package01Icon,
  invoice: Invoice01Icon,
  project_invoice: Invoice02Icon,
}

const GROUPS: { key: keyof SearchResults; heading: string }[] = [
  { key: "orders", heading: "Orders" },
  { key: "invoices", heading: "Invoices" },
  { key: "project_invoices", heading: "Project invoices" },
  { key: "projects", heading: "Projects" },
  { key: "proposals", heading: "Proposals" },
  { key: "change_orders", heading: "Change orders" },
  { key: "accounts", heading: "Accounts" },
  { key: "vendors", heading: "Vendors" },
  { key: "contacts", heading: "Contacts" },
  { key: "customers", heading: "Customers" },
  { key: "companies", heading: "Companies" },
  { key: "products", heading: "Products" },
]

const NEGATIVE_STATUSES = new Set([
  "OVERDUE",
  "FAILED",
  "DECLINED",
  "VOID",
  "LATE",
  "CANCELLED",
  "LOST",
])
const POSITIVE_STATUSES = new Set([
  "PAID",
  "COMPLETED",
  "SHIPPED",
  "APPROVED",
  "ACTIVE",
  "WON",
  "DONE",
])

function StatusBadge({ status }: { status: string }) {
  const key = status.toUpperCase().replace(/ /g, "_")
  if (NEGATIVE_STATUSES.has(key)) {
    return <Badge variant="destructive">{status}</Badge>
  }
  if (POSITIVE_STATUSES.has(key)) {
    return (
      <Badge variant="outline" className="text-emerald-600 dark:text-emerald-400">
        {status}
      </Badge>
    )
  }
  return <Badge variant="secondary">{status}</Badge>
}

function ResultRow({
  item,
  onSelect,
}: {
  item: SearchResultItem
  onSelect: (url: string) => void
}) {
  return (
    <CommandItem
      value={`${item.type}-${item.id}`}
      onSelect={() => onSelect(item.url)}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <HugeiconsIcon
          icon={TYPE_ICONS[item.type]}
          size={14}
          className="shrink-0 text-muted-foreground"
        />
        <span className="shrink-0 font-medium">{item.title}</span>
        {item.meta.map((part, i) => (
          <span key={i} className="flex min-w-0 items-center gap-2">
            <span className="text-border">|</span>
            <span className="truncate text-muted-foreground">{part}</span>
          </span>
        ))}
      </div>
      {item.status && (
        <span className="flex shrink-0 justify-end pl-2">
          <StatusBadge status={item.status} />
        </span>
      )}
    </CommandItem>
  )
}

export function GlobalSearchCommand() {
  const navigate = useNavigate()
  const { role, isAdmin } = useAuth()
  const { isOpen, close, toggle } = useSearchStore()
  const [inputValue, setInputValue] = useState("")
  const [buildOrderOpen, setBuildOrderOpen] = useState(false)

  const debouncedQuery = useDebounce(inputValue, 300)
  const { data, isFetching } = useGlobalSearch(debouncedQuery, isOpen)

  const canBuildOrder = role !== "STAFF"

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        toggle()
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [toggle])

  const closePalette = () => {
    close()
    setInputValue("")
  }

  const goTo = (url: string) => {
    closePalette()
    navigate(url)
  }

  const isRecentMode = debouncedQuery.length < 2
  const results = data?.results
  const hasResults =
    !!results && GROUPS.some(({ key }) => results[key].length > 0)

  return (
    <>
      <CommandDialog
        title="Global Search"
        description="Search orders, invoices, projects, accounts, and more"
        open={isOpen}
        onOpenChange={(open: boolean) => !open && closePalette()}
        className="top-14 max-w-3xl translate-y-0 sm:max-w-3xl"
      >
        {/* Results come pre-filtered + ranked by the backend; cmdk must not re-filter. */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Navigate. Find orders, invoices, contacts, and more."
            value={inputValue}
            onValueChange={setInputValue}
          />
          <CommandList className="max-h-[60vh]">
            {!hasResults && (
              <CommandEmpty>
                {isFetching ? "Searching…" : "No results found."}
              </CommandEmpty>
            )}
            <CommandGroup heading="Quick Actions">
              {canBuildOrder && (
                <CommandItem
                  onSelect={() => {
                    closePalette()
                    setBuildOrderOpen(true)
                  }}
                >
                  <HugeiconsIcon icon={Add01Icon} size={14} />
                  Build Order
                </CommandItem>
              )}
              {isAdmin && (
                <>
                  <CommandItem onSelect={() => goTo("/crm/accounts/new")}>
                    <HugeiconsIcon icon={UserAdd01Icon} size={14} />
                    New Account
                  </CommandItem>
                  <CommandItem onSelect={() => goTo("/crm/contacts/new")}>
                    <HugeiconsIcon icon={ContactBookIcon} size={14} />
                    New Contact
                  </CommandItem>
                </>
              )}
            </CommandGroup>
            {hasResults && <CommandSeparator />}
            {results &&
              GROUPS.map(({ key, heading }) => {
                const items = results[key]
                if (items.length === 0) return null
                return (
                  <CommandGroup
                    key={key}
                    heading={isRecentMode ? `Recent ${heading.toLowerCase()}` : heading}
                  >
                    {items.map((item) => (
                      <ResultRow
                        key={`${item.type}-${item.id}`}
                        item={item}
                        onSelect={goTo}
                      />
                    ))}
                  </CommandGroup>
                )
              })}
          </CommandList>
        </Command>
      </CommandDialog>
      <CreateOrderDialog open={buildOrderOpen} onOpenChange={setBuildOrderOpen} />
    </>
  )
}
